<script setup>
/**
 * GiftPreview: exactly what the recipient receives, inside the send modal
 * (2026-10-21; 2026-10-22 gift redesign, `gift-flow-review-2026-10-22/
 * design/DESIGN.md` §5).
 *
 * The letter (GiftLetter, their version: the picture, the note, the title,
 * each card with its short line and passage, "All four together"), ending on
 * the reply card, drawn but not pressable (`room.respond`); then the room:
 * the Mystic's first message and the two chips they will have, both from the
 * server's `room` (the same words the room says, build/API.md §1). It ends on
 * "That's everything."
 *
 * On a phone it is a sheet over the send modal ("Read it as they will ›",
 * Back, "Back to sending"); on a computer it is the left pane, read-only, the
 * note mirroring the composer as it is typed.
 *
 * The words come from `care-package/preview/` (written when the modal opened,
 * once). Until they arrive the letter shows what is already known and one
 * quiet line says the Mystic is writing it. An older server without `room`
 * falls back to `first_message` and the app's own chips.
 */
import { computed } from 'vue'
import { letterVersions } from '@/utils/gift-letter'
import { giftChips } from '@/utils/care-room'
import { everythingLine, possessive } from '@/utils/gift-send'
import GiftLetter from '@/components/ui/GiftLetter.vue'
import GiftReply from '@/components/ui/GiftReply.vue'

const props = defineProps({
  // care-package/preview/ answer: { letter, first_message, room } (null while it is written)
  preview: { type: Object, default: null },
  image: { type: String, default: '' },
  note: { type: String, default: '' },
  fromName: { type: String, default: '' },
  forName: { type: String, default: '' },
  // 'sheet' (a phone: over the modal, with Back) | 'pane' (a computer's left pane)
  variant: { type: String, default: 'sheet' },
})
const emit = defineEmits(['back'])

const letter = computed(() => (props.preview && props.preview.letter) || null)
const room = computed(() => (props.preview && props.preview.room) || null)
const hers = computed(() => letterVersions(letter.value || {}).hers)
const writing = computed(() => !letter.value)
const firstMessage = computed(() => ((room.value && room.value.first_message) || (props.preview && props.preview.first_message) || '').trim())
const chips = computed(() => {
  const served = room.value && Array.isArray(room.value.chips) ? room.value.chips : []
  const fromServer = served.map((c, i) => (typeof c === 'string' ? { key: `c${i}`, label: c, primary: i === 0 } : c))
    .filter((c) => c && (c.label || '').trim())
  return fromServer.length ? fromServer : giftChips({ from_name: props.fromName }, {})
})
// The reply card is the room's own GiftReply, inert: the same words and the
// same look they will see at the letter's end (build/API.md §1 fallbacks).
const replyGift = computed(() => ({ from_name: props.fromName, for_name: props.forName }))
const name = computed(() => (props.forName || '').trim() || 'They')
const sheet = computed(() => props.variant === 'sheet')
const whose = computed(() => ((props.forName || '').trim() ? possessive(props.forName) : 'their'))
</script>

<template>
  <section class="gp" :class="['gp-' + variant]" :aria-label="`What ${name} receives`" data-testid="gift-preview">
    <header class="gp-head">
      <p class="gp-eyebrow">What {{ name }} opens</p>
      <button v-if="sheet" type="button" class="gp-back" data-testid="gift-preview-back" @click="emit('back')">
        <i class="fas fa-chevron-down" aria-hidden="true"></i> Back
      </button>
      <span v-else class="gp-scroll" aria-hidden="true">Scroll to read all of it</span>
    </header>

    <GiftLetter tone="hers" :image="(letter && letter.image_url) || image" :note="note" :from-name="fromName"
                :title="hers.title" :caption="hers.caption || ''" :reader-name="(letter && letter.reader_name) || ''"
                :reader-avatar="(letter && letter.reader_avatar) || ''" :cards="hers.cards" :summary="hers.summary">
      <template v-if="!writing" #end>
        <!-- The reply card, drawn as they will see it: the room's own, not pressable here. -->
        <div class="gp-reply" aria-hidden="true" data-testid="gift-preview-reply">
          <GiftReply :gift="replyGift" :room="room || {}" inert />
        </div>
      </template>
    </GiftLetter>

    <p v-if="writing" class="gp-writing" role="status" data-testid="gift-preview-writing">
      <span class="gp-dot" aria-hidden="true">✦</span> The Mystic is writing {{ whose }} version…
    </p>

    <template v-else>
      <p class="gp-label">Then, in the room</p>
      <div v-if="firstMessage" class="gp-room" data-testid="gift-preview-first-message">
        <img v-if="letter.reader_avatar" class="gp-avatar" :src="letter.reader_avatar" alt="" />
        <span v-else class="gp-avatar gp-avatar-blank" aria-hidden="true">✦</span>
        <p class="gp-bubble">{{ firstMessage }}</p>
      </div>
      <!-- Her two buttons, as she will see them: shown, not pressable. -->
      <ul class="gp-chips" :aria-label="`The two buttons ${name} will have`" data-testid="gift-preview-chips">
        <li v-for="c in chips" :key="c.key" class="gp-chip" :class="{ primary: c.primary }">{{ c.label }}</li>
      </ul>

      <footer class="gp-end">
        <p class="gp-label">That's everything</p>
        <p class="gp-end-line">{{ everythingLine(forName) }}</p>
        <button v-if="sheet" type="button" class="gp-return" data-testid="gift-preview-return" @click="emit('back')">
          Back to sending
        </button>
      </footer>
    </template>
  </section>
</template>

<style scoped>
.gp {
  --ink: #2a1845; --muted: #6b5a82; --gold-dk: #8a6a2a; --amethyst: #4e2776; --violet: #7b4bb0;
  --ui: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --display: 'Letter Display', 'Cinzel', Georgia, serif;
  color: var(--ink); font-family: var(--ui);
}
.gp-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 44px; margin: 0 0 10px; }
.gp-eyebrow, .gp-label { margin: 0; font: 600 11px/1.4 var(--display); letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold-dk); }
.gp-scroll { font: 500 12px var(--ui); color: var(--muted); }
.gp-back {
  min-height: 44px; padding: 0 16px; border-radius: 999px; border: 1.5px solid #d9c9ef; background: #fff; cursor: pointer;
  font: 700 13.5px var(--ui); color: var(--amethyst); display: inline-flex; align-items: center; gap: 6px;
}
.gp-back i { font-size: 10px; }
.gp-writing { margin: 14px 4px 0; font: 500 13.5px/1.5 var(--ui); color: var(--muted); text-align: center; }
.gp-dot { display: inline-block; color: #c9a94e; animation: gp-breathe 1.4s ease-in-out infinite; }
@keyframes gp-breathe { 50% { opacity: 0.4; } }

.gp-label { text-align: center; margin-top: 22px; }
.gp-room { display: flex; align-items: flex-start; gap: 10px; margin: 12px 0 0; }
.gp-avatar { flex: none; width: 34px; height: 34px; border-radius: 50%; object-fit: cover; border: 1px solid #c9a94e; }
.gp-avatar-blank { display: grid; place-items: center; background: #2a1845; color: #f3d98f; font-size: 13px; }
.gp-bubble { margin: 0; min-width: 0; padding: 12px 14px; border-radius: 4px 16px 16px 16px; background: #2a1845; color: #f4ecff;
  font: 500 15px/1.45 var(--ui); text-wrap: pretty; overflow-wrap: break-word; }
.gp-chips { list-style: none; margin: 10px 0 0 44px; padding: 0; display: flex; flex-wrap: wrap; gap: 8px; }
.gp-chip { padding: 9px 14px; border-radius: 999px; border: 1.5px solid #d9c9ef; background: #fff; color: var(--amethyst);
  font: 600 13px/1.2 var(--ui); }
.gp-chip.primary { background: linear-gradient(180deg, #7b4bb0, #4e2776); border-color: transparent; color: #ffdfa3; }

.gp-reply { margin: 8px 0 0; }
.gp-reply :deep(.gr) { margin: 26px 4px 6px; }
.gp-end { margin: 8px 0 0; padding: 4px 0 6px; border-top: 1px solid rgba(201, 169, 78, 0.35); text-align: center; }
.gp-end-line { margin: 6px auto 0; max-width: 40ch; font: italic 500 16px/1.45 var(--serif); color: var(--muted); text-wrap: balance; }
.gp-return {
  display: block; width: 100%; margin: 18px 0 0; min-height: 52px; border-radius: 999px; border: 0; cursor: pointer;
  background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #ffdfa3; font: 700 15px var(--ui);
}
.gp-back:focus-visible, .gp-return:focus-visible { outline: 2px solid var(--violet); outline-offset: 3px; }
@media (prefers-reduced-motion: reduce) { .gp-dot { animation: none; } }
</style>
